import {
  Box,
  Stack,
  OutlinedInput,
  Divider,
  Button,
  Typography,
  Link,
  IconButton,
  useTheme,
  Slide,
  Tooltip,
} from "@mui/material";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import React, { FC, useContext, useEffect, useMemo, useRef } from "react";
import { assetService, cacheService } from "../../../Core/Api/services";
import {
  ArrowBackOutlined,
  BookmarkBorderOutlined,
  ChevronRightOutlined,
  DeleteOutlined,
  EditOutlined,
  FileDownloadOutlined,
  SearchOutlined,
  ShareOutlined,
  ShoppingBagOutlined,
  TimerOutlined,
  VideoLibraryOutlined,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { GetFrameRate, GetIsFileTCDisplayed, GetTimecodeIn } from "../../../Redux/Reducers/redux-slice-react-player";
import { TutorialContext } from "../../../Utils/Helpers/tutorial";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import ClipCard, { ClipCardProps } from "../../Cards/ClipCard/clip-card";
import { useSelection } from "../../../Utils/Hooks/useSelection";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
  GetCreatingOrEditingTimelineEvent,
  GetFilteredTimelineClips,
  GetFocusedTimelineClip,
  GetTimelineClips,
  SetActiveTimeline,
  SetCreatingOrEditingTimelineEvent,
  SetFilteredTimelineClips,
  SetFocusedTimelineClip,
  SetTimelineClips,
  TimelineType,
} from "../../../Redux/Reducers/redux-slice-timeline";
import { useSelector } from "react-redux";
import EmptyListSlot from "../../Misc/EmptyListSlot";
import { IPreviewTabContentProps } from "./preview-tabs";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { FrameToTimecode, JWPMarkerTimecode, TimecodeToFileTimecode } from "../../../Utils/Timecode/ReactPlayer";
import moment from "moment";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import { ModalContext } from "../../Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import {
  ModalDownloadClip,
  ModalDownloadClipElementProps,
  ModalDownloadClipProps,
} from "../../Modals/ModalDownloadClip/modal-download-clip";

export const ClipsTab: React.FC<IPreviewTabContentProps> = (props: IPreviewTabContentProps) => {
  // Props & Hooks
  const { asset } = props;
  const containerRef = useRef(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tutorialContext = React.useContext(TutorialContext);
  const clips = useSelector(GetTimelineClips);
  const filteredClips = useSelector(GetFilteredTimelineClips);
  const focusedClip = useSelector(GetFocusedTimelineClip);
  const enqueueCustomSnackbar = useCustomSnackbar();

  // States
  //   const [isCreatingClip, SetIsCreatingClip] = React.useState(false);
  const { isSelected: isExpanded, select: expand } = useSelection("0");
  //   const [isCreatingComment, SetIsCreatingComment] = React.useState(false);
  const creatingOrEditingTimelineEvent = useSelector(GetCreatingOrEditingTimelineEvent);
  const frameRate = useAppSelector(GetFrameRate);
  const timecodeIn = useAppSelector(GetTimecodeIn);

  const [searchClipsPatern, SetSearchClipsPatern] = React.useState<string>("");
  const [frameDuration, SetFrameDuration] = React.useState<number>(0);
  const [clipsTimecodeList, SetClipsTimecodeList] = React.useState<ClipCardProps[]>([]);
  const [showTuto, setShowTuto] = React.useState<boolean>(false);
  const { setTutorialPage } = { ...tutorialContext };
  const [helpPopupAnchorEl, setHelpPopupAnchorEl] = React.useState<HTMLElement | null>(null);

  // Cache
  const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();
  const frameRateAsset: number =
    asset && metadata.length > 0 ? GetMetadataByTagName(asset!, "techFrameRate", metadata)?.value : 25;
  const isFileTCDisplayed = useAppSelector(GetIsFileTCDisplayed);

  // Events
  const SearchChange = (event) => {
    const {
      target: { value },
    } = event;
    SetSearchClipsPatern(value.toLocaleLowerCase());
  };

  const handleHelpClick: React.MouseEventHandler<HTMLElement> = (e) => {
    if (showTuto) {
      if (!setTutorialPage) return;

      setTutorialPage("player");

      e.stopPropagation();
      setHelpPopupAnchorEl(e.currentTarget);
    }
  };

  const FilterClips = (clips: ClipCardProps[], isFileTCDisplayed: boolean, patern?: string) => {
    var result = clips;
    if (patern != null)
      result = result.filter(
        (x) =>
          x.clipName?.toLocaleLowerCase().includes(patern) ||
          x.clipDescription?.toLocaleLowerCase().includes(patern) ||
          x.createdBy?.toLocaleLowerCase().includes(patern) ||
          x.createdAt?.toString().toLocaleLowerCase().includes(patern)
      );

    return result;
  };

  React.useEffect(() => {
    dispatch(SetFilteredTimelineClips(FilterClips(clips, isFileTCDisplayed, searchClipsPatern)));
  }, [clips, clips.length, frameDuration, searchClipsPatern]);

  React.useEffect(() => {
    dispatch(SetActiveTimeline(TimelineType.Clips));
  }, []);

  return (
    <Stack direction="row" height="100%" width={"100%"} alignItems={"stretch"} ref={containerRef}>
      <Slide in={focusedClip === null} direction="right" container={containerRef.current}>
        <Stack sx={{ alignItems: "stretch", width: "100%" }}>
          {clips.length > 0 ? (
            <>
              <Box sx={{ pt: 2, px: 2 }}>
                <OutlinedInput
                  id="clips"
                  label={t("common:standard")}
                  size="small"
                  placeholder={t("common:search")}
                  onChange={SearchChange}
                  value={searchClipsPatern}
                  endAdornment={<SearchOutlined />}
                  fullWidth
                />
              </Box>
              <Stack
                gap={1}
                mt={1}
                pt={1}
                pb={2}
                px={2}
                sx={{
                  overflow: "scroll",
                  flex: "1 1 0",
                }}
              >
                {filteredClips.map((clip, index) => (
                  <ClipCard
                    key={clip.id}
                    expandable
                    playable
                    {...clip}
                    // isPlaying={isPlaying(clip.id.toString())}
                    onClickMoreInfos={() => {
                      dispatch(SetFocusedTimelineClip(clip));
                    }}
                    isPlaying={true}
                    deleteCallback={() => {
                      assetService.DeleteObjectById(clip.id).then((response) => {
                        if (response.success) {
                          enqueueCustomSnackbar(t("alert:error.clipDeleted"), "success");
                          const clipsTemp = [...clips];
                          clipsTemp.splice(clips.indexOf(clips[index]), 1);
                          dispatch(SetTimelineClips(clipsTemp));
                        } else
                          enqueueCustomSnackbar(
                            t("alert:error.deletingClip", { message: response.message }),
                            "warning"
                          );
                      });
                    }}
                  />
                ))}
              </Stack>
              <Box bgcolor="background.paper">
                <Divider />
                <Box flex={1} p={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<VideoLibraryOutlined />}
                    onClick={(e) => {
                      // ajouté ?
                      dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true }));
                    }}
                    disabled={!UserHasAcl(AclTypes.aclClipCreate) || creatingOrEditingTimelineEvent}
                  >
                    {t("common:createClip")}
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Stack p={2}>
              <EmptyListSlot
                title={t("page:emptyListSlot.title.noClips")}
                icon={<VideoLibraryOutlined />}
                CTA={
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true }));
                    }}
                    disabled={!UserHasAcl(AclTypes.aclClipCreate) || creatingOrEditingTimelineEvent}
                  >
                    {t("common:createClip")}
                  </Button>
                }
              />
            </Stack>
          )}
        </Stack>
      </Slide>
      <Slide in={focusedClip != null} direction="left" container={containerRef.current}>
        <Stack sx={{ position: "absolute", width: "100%" }}>
          {focusedClip !== null && <ClipSingleTab {...focusedClip} />}
        </Stack>
      </Slide>
    </Stack>
  );
};

const ClipSingleTab: FC<ClipCardProps> = (props) => {
  const { clipName, clipDescription, timecodes, createdAt, createdBy, id } = props;
  const frameRate = useAppSelector(GetFrameRate);
  const dispatch = useAppDispatch();
  const { addObjectByIdToBasket } = useBasketActions();
  const { AddItemByIdToCollection } = useCollectionActions();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const timelineClips = useAppSelector(GetTimelineClips);
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const theme = useTheme();
  const { t } = useTranslation();
  const isCreatingTimelineEvent = useAppSelector(GetCreatingOrEditingTimelineEvent);

  const clipDuration = useMemo(() => {
    return ((timecodes.out.markFrame - timecodes.in.markFrame) / frameRate).toFixed(0);
  }, [timecodes, frameRate]);

  useEffect(() => {
    return () => {
      dispatch(SetFocusedTimelineClip(null));
    };
  }, []);

  const handleDeleteClip = (clipId: number) => {
    assetService.DeleteObjectById(clipId).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:error.clipDeleted"), "success");
        dispatch(SetTimelineClips(timelineClips.filter((clip) => clip.id !== clipId)));
        dispatch(SetFocusedTimelineClip(null));
      } else enqueueCustomSnackbar(t("alert:error.deletingClip", { message: response.message }), "warning");
    });
  };

  const handleDownloadClip = (clipId: number) => {
    if (!openModal) return;

    openModal<ModalDownloadClipElementProps>(ModalDownloadClip, {
      clipId: clipId,
    });
  };

  return (
    <Stack p={2} gap={2}>
      <Link
        href="#"
        underline="hover"
        color={theme.palette.text.secondary}
        onClick={() => dispatch(SetFocusedTimelineClip(null))}
      >
        <ArrowBackOutlined sx={{ mr: 0.5 }} />
        Back
      </Link>
      <Stack gap={1}>
        <Stack gap={0.5}>
          <Typography variant="subtitle1" mb={0.5} fontWeight={600} color="text.primary">
            {clipName}
          </Typography>

          <Stack direction="row" alignItems="center">
            <JWPMarkerTimecode frameRate={frameRate} {...timecodes.in}></JWPMarkerTimecode>
            <ChevronRightOutlined fontSize="small" sx={(t) => ({ color: t.palette.text.tier })} />
            <JWPMarkerTimecode frameRate={frameRate} {...timecodes.out}></JWPMarkerTimecode>
            <Stack sx={{ ml: 1 }} direction="row" spacing={0.5}>
              <TimerOutlined fontSize="small" sx={(t) => ({ color: t.palette.text.tier })} />
              <Typography variant="body1" color="text.secondary">
                {clipDuration}s
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        {clipDescription && (
          <Typography variant="body1" color="text.secondary">
            {clipDescription}
          </Typography>
        )}
      </Stack>
      {(createdBy || createdAt) && (
        <Stack gap={0.5}>
          {createdBy && (
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.tier">{t("common:createdBy")}:</Typography>
              <Typography color="text.secondary">{createdBy}</Typography>
            </Stack>
          )}
          {createdAt && (
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.tier">{t("common:createdAtLowerCase")}:</Typography>
              <Typography color="text.secondary">
                {moment(createdAt).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      <Stack gap={1}>
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            fullWidth
            startIcon={<EditOutlined />}
            onClick={() => {
              dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true, event: props }));
            }}
          >
            {t("common:editUpperCase")}
          </Button>
          <Button
            onClick={(e) => {
              if (!openModal) return;

              openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
                displayComment: false,
                commentLabel: t("modal:message.confirmDeleteClip"),
                title: t("modal:title.deleteThisClip"),
                onConfirm: () => handleDeleteClip(id),
              });
            }}
            variant="outlined"
            size="small"
            color="error"
            fullWidth
            startIcon={<DeleteOutlined />}
          >
            {t("common:delete")}
          </Button>
        </Stack>
        <Stack direction={"row"} spacing={1}>
          <Tooltip title={t("common:download")}>
            <IconButton sx={{ flexGrow: 1 }} size="small" color="neutral" onClick={() => handleDownloadClip(id)}>
              <FileDownloadOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("button:addToCollection")}>
            <IconButton sx={{ flexGrow: 1 }} size="small" color="neutral" onClick={() => AddItemByIdToCollection(id)}>
              <BookmarkBorderOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("button:addToOrder")}>
            <IconButton
              sx={{ flexGrow: 1 }}
              size="small"
              color="neutral"
              onClick={() => {
                addObjectByIdToBasket(id);
              }}
            >
              <ShoppingBagOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
};
